import { createContext, useContext, useEffect, ReactNode } from "react";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { Score } from "src/types";
import { Post } from "src/api/client-api/post/postSchema";
import { useGetCompanyPost } from "src/api/client-api/post/useGetCompanyPost";
import { useEntityBasedScoreParams } from "src/context/EntityBasedScore/useEntityBasedScoreParams";
import { useQueryClient } from "react-query";

type PuckCompanyScoreContextValue = {
    isLoading: boolean;
    isError: boolean;
    companyScores: Score[];
    companyPost?: Post | undefined;
    selectedVariation: { scoreId: number; variationId: number | null } | undefined;
    setVariation: (scoreId: number, variationId: number | null) => void;
};

const PuckCompanyScoreContext = createContext<PuckCompanyScoreContextValue | null>(null);

type PuckCompanyScoreProviderProps = {
    children: ReactNode;
};

export const PuckCompanyScoreProvider = (props: PuckCompanyScoreProviderProps) => {
    const { children } = props;
    const { selectedCompany, currentCampaign, scores = [] } = useFoodpilotContext();
    const companyId = selectedCompany?.id ? Number(selectedCompany.id) : undefined;
    const campaignId = currentCampaign?.id ? Number(currentCampaign.id) : undefined;

    const scoreQuery = useEntityBasedScoreParams({
        campaign: currentCampaign?.id,
    });

    const queryClient = useQueryClient();

    const {
        post: companyPost,
        isLoading: isPostLoading,
        error: isPostError,
    } = useGetCompanyPost({
        companyId,
        campaignId,
        scoreQuery: {
            dimensions: ["headings"],
            variations:
                (
                    scoreQuery.state.lastSelectedVariation &&
                    scoreQuery.state.lastSelectedVariation.scoreVariationId !== null
                ) ?
                    {
                        [scoreQuery.state.lastSelectedVariation.scoreId]:
                            scoreQuery.state.lastSelectedVariation.scoreVariationId,
                    }
                :   {},
        },
    });

    useEffect(() => {
        if (isPostError) {
            queryClient.cancelQueries({ queryKey: ["getCompanyPost", companyId, campaignId] });
        }
    }, [isPostError, queryClient, companyId, campaignId]);

    const setVariation = (scoreId: number, variationId: number | null) => {
        scoreQuery.state.selectVariation(scoreId, variationId);
    };

    const contextValue: PuckCompanyScoreContextValue = {
        isLoading: isPostLoading,
        isError: !!isPostError,
        companyScores: scores,
        companyPost,
        selectedVariation:
            scoreQuery.state.lastSelectedVariation ?
                {
                    scoreId: scoreQuery.state.lastSelectedVariation.scoreId,
                    variationId:
                        scoreQuery.state.lastSelectedVariation.scoreVariationId !== null ?
                            scoreQuery.state.lastSelectedVariation.scoreVariationId
                        :   null,
                }
            :   undefined,
        setVariation,
    };

    // // @https://github.com/Ilyomix: Implement a skeleton loader component for better look and feel
    // if (isPostLoading) {
    //     return (
    //         <Box sx={{ display: "flex", justifyContent: "center", p: theme.spacing(3) }}>
    //             <CircularProgress size={24} sx={{ mr: theme.spacing(2) }} />
    //         </Box>
    //     );
    // }

    // // @https://github.com/Ilyomix: Implement error handling matching the design system
    // if (isPostError || !companyId || !campaignId) {
    //     return <></>;
    // }

    return <PuckCompanyScoreContext.Provider value={contextValue}>{children}</PuckCompanyScoreContext.Provider>;
};

export const usePuckCompanyScore = () => {
    const context = useContext(PuckCompanyScoreContext);

    if (!context) {
        throw new Error("usePuckCompanyScore must be used within a PuckCompanyScoreProvider");
    }

    return context;
};
